import { useState, useRef, useEffect } from "react";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Plus, Zap } from "lucide-react";
import { format } from "date-fns";

/**
 * QuickEntryForm - Formulario de entrada rápida para transacciones
 * 
 * Para desactivar este componente, simplemente comenta o elimina
 * la línea donde se importa y usa en TransactionsPage.jsx
 */
const QuickEntryForm = ({ categories, paymentMethods, branches = [], onSave, defaultType = "expense" }) => {
  const [form, setForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    description: "",
    amount: "",
    category_id: "",
    payment_method_id: "",
    branch_id: "",
    type: defaultType
  });
  const [saving, setSaving] = useState(false);
  
  // Refs for keyboard navigation
  const dateRef = useRef(null);
  const descriptionRef = useRef(null);
  const amountRef = useRef(null);
  const categoryRef = useRef(null);
  const paymentRef = useRef(null);
  const branchRef = useRef(null);

  // Focus description on mount and after save
  useEffect(() => {
    descriptionRef.current?.focus();
  }, []);

  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Tab" && !e.shiftKey) {
      // Let Tab work normally
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef) {
        nextRef.current?.focus();
      } else {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.description.trim()) {
      descriptionRef.current?.focus();
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      amountRef.current?.focus();
      return;
    }
    if (!form.category_id) {
      categoryRef.current?.focus();
      return;
    }
    if (!form.branch_id) {
      branchRef.current?.focus();
      return;
    }

    setSaving(true);
    try {
      // Use first payment method if none selected
      const payment_method_id = form.payment_method_id || (paymentMethods[0]?.id || "");
      
      await onSave({
        ...form,
        payment_method_id,
        amount: parseFloat(form.amount),
        invoiced: false,
        asset: "",
        notes: ""
      });
      
      // Reset form but keep date, category, payment method, and type
      setForm(prev => ({
        ...prev,
        description: "",
        amount: ""
      }));
      
      // Focus back on description for next entry
      setTimeout(() => descriptionRef.current?.focus(), 100);
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setSaving(false);
    }
  };

  // Since this page is only for expenses, filter to expense categories
  const filteredCategories = categories.filter(c => c.type === 'expense');

  return (
    <div className="bg-gradient-to-r from-[#FFF5F5] to-[#FFE4E6] border border-[#FECDD3] rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-[#F43F5E]" />
        <span className="text-sm font-medium text-[#4A4A4A]">Entrada Rápida</span>
        <span className="text-xs text-[#6B7280]">Tab para siguiente campo, Enter para guardar</span>
      </div>
      
      <div className="flex flex-wrap items-end gap-2">
        {/* Type Label (fixed to Gasto) */}
        <div className="w-20 h-9 px-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md flex items-center justify-center">
          <span className="text-sm font-medium text-[#EF4444]">Gasto</span>
        </div>

        {/* Date */}
        <div className="w-40">
          <Input
            ref={dateRef}
            type="date"
            value={form.date}
            onChange={(e) => setForm({...form, date: e.target.value})}
            onKeyDown={(e) => handleKeyDown(e, descriptionRef)}
            className="h-9 bg-white border-[#FECDD3] text-sm"
          />
        </div>

        {/* Description */}
        <div className="flex-1 min-w-[200px]">
          <Input
            ref={descriptionRef}
            placeholder="Descripción..."
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            onKeyDown={(e) => handleKeyDown(e, amountRef)}
            className="h-9 bg-white border-[#FECDD3] text-sm"
          />
        </div>

        {/* Amount */}
        <div className="w-28">
          <Input
            ref={amountRef}
            type="number"
            step="0.01"
            placeholder="Monto"
            value={form.amount}
            onChange={(e) => setForm({...form, amount: e.target.value})}
            onKeyDown={(e) => handleKeyDown(e, categoryRef)}
            className="h-9 bg-white border-[#FECDD3] text-sm font-mono"
          />
        </div>

        {/* Category */}
        <div className="w-40">
          <Select 
            value={form.category_id} 
            onValueChange={(v) => setForm({...form, category_id: v})}
          >
            <SelectTrigger 
              ref={categoryRef}
              className="h-9 bg-white border-[#FECDD3] text-sm"
              onKeyDown={(e) => handleKeyDown(e, paymentRef)}
            >
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {filteredCategories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: cat.color}} />
                    {cat.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Payment Method */}
        <div className="w-36">
          <Select 
            value={form.payment_method_id} 
            onValueChange={(v) => setForm({...form, payment_method_id: v})}
          >
            <SelectTrigger 
              ref={paymentRef}
              className="h-9 bg-white border-[#FECDD3] text-sm"
              onKeyDown={(e) => handleKeyDown(e, branchRef)}
            >
              <SelectValue placeholder="Método pago" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {paymentMethods.map((pm) => (
                <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Branch (Sucursal) */}
        <div className="w-40">
          <Select
            value={form.branch_id}
            onValueChange={(v) => setForm({...form, branch_id: v})}
          >
            <SelectTrigger
              ref={branchRef}
              className="h-9 bg-white border-[#FECDD3] text-sm"
              onKeyDown={(e) => handleKeyDown(e, null)}
              data-testid="quick-entry-branch"
            >
              <SelectValue placeholder="Sucursal" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                    {b.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="h-9 px-4 bg-[#F43F5E] hover:bg-[#E11D48] text-white"
        >
          <Plus className="w-4 h-4 mr-1" />
          {saving ? "..." : "Agregar"}
        </Button>
      </div>
    </div>
  );
};

export default QuickEntryForm;
